import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styles from "./beneficiary-select-modal.module.scss";
import {
	BeneficiaryItem,
	useGetBeneficiariesQuery,
	useDeleteBeneficiaryMutation,
} from "../../../../../service/beneficiary-service";
import { ConsumerType } from "../../../../../model/energy-profile";
import { getErrorMessage } from "../../../../../utils/error-handler";

interface BeneficiarySelectModalProps {
	paymentType: "prepaid" | "postpaid" | null;
	onSelect: (beneficiary: BeneficiaryItem) => void;
	onClose: () => void;
}

const typeLabelKey = (type: ConsumerType) =>
	type === ConsumerType.Prepaid ? "common.labels.prepaid" : type === ConsumerType.Postpaid ? "common.labels.postpaid" : "common.labels.unknown";

const matchesType = (b: BeneficiaryItem, paymentType: "prepaid" | "postpaid" | null) => {
	if (!paymentType) return true;
	return paymentType === "prepaid"
		? b.type === ConsumerType.Prepaid
		: b.type === ConsumerType.Postpaid;
};

export const BeneficiarySelectModal: React.FC<BeneficiarySelectModalProps> = ({
	paymentType,
	onSelect,
	onClose,
}) => {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useGetBeneficiariesQuery();
	const [deleteBeneficiary, { isLoading: isDeleting }] = useDeleteBeneficiaryMutation();

	const filtered = (data ?? []).filter(b => matchesType(b, paymentType));

	const handleDelete = async (e: React.MouseEvent, id: string) => {
		e.stopPropagation();
		try {
			await deleteBeneficiary(id).unwrap();
			toast.success(t("buyElectricity.web.beneficiaryRemoved"));
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.web.failedToRemoveBeneficiary"));
		}
	};

	return (
		<div className={styles.overlay} onClick={onClose}>
			<div className={styles.modal} onClick={e => e.stopPropagation()}>
				<header className={styles.header}>
					<button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("buyElectricity.web.close")}>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
							<path
								d="M12 4 4 12M4 4l8 8"
								stroke="var(--app-primary-text)"
								strokeWidth="1.5"
								strokeLinecap="round"
							/>
						</svg>
					</button>
					<span className={styles.title}>{t("common.labels.savedBeneficiaries")}</span>
					<div className={styles.headerSpacer} />
				</header>
				<hr className={styles.divider} />

				<div className={styles.body}>
					{isLoading && (
						<div className={styles.statusBlock}>
							<span className={styles.spinner} />
							<span>{t("buyElectricity.web.loadingBeneficiaries")}</span>
						</div>
					)}

					{!isLoading && isError && (
						<div className={styles.statusBlock}>
							<span>{t("buyElectricity.web.couldntLoadBeneficiaries")}</span>
							<button type="button" className={styles.retryBtn} onClick={() => refetch()}>
								{t("energyManagement.retry")}
							</button>
						</div>
					)}

					{!isLoading && !isError && filtered.length === 0 && (
						<div className={styles.statusBlock}>
							<span className={styles.emptyTitle}>
								{paymentType
									? t("buyElectricity.web.noSavedBeneficiariesForType", { type: paymentType })
									: t("buyElectricity.web.noSavedBeneficiariesYet")}
							</span>
							<span className={styles.emptyHint}>
								{t("buyElectricity.web.saveMeterHint")}
							</span>
						</div>
					)}

					{!isLoading && filtered.length > 0 && (
						<ul className={styles.list}>
							{filtered.map(b => (
								<li key={b.id}>
									<button
										type="button"
										className={styles.row}
										onClick={() => onSelect(b)}
									>
										<div className={styles.rowMain}>
											<span className={styles.rowName}>{b.customerName || b.identifier}</span>
											<span className={styles.rowMeta}>
												<span className={styles.typePill}>{t(typeLabelKey(b.type))}</span>
												<span className={styles.rowIdentifier}>{b.identifier}</span>
											</span>
											{b.address && <span className={styles.rowAddress}>{b.address}</span>}
										</div>
										<button
											type="button"
											className={styles.removeBtn}
											disabled={isDeleting}
											onClick={e => handleDelete(e, b.id)}
											aria-label={t("beneficiary.removeBeneficiary")}
										>
											{t("buyElectricity.web.remove")}
										</button>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
};
